// F2 Parte C2 — lista de resets de credenciais pendentes (lado da 2ª aprovação).
//
// Um 2º admin (distinto de quem abriu) vê os pedidos pendentes, aprova com TOTP
// e, na execução, recebe o LINK de primeiro acesso (reusa comiteFirstAccessUrl
// da F1 Parte 3) para entregar ao membro. Também permite cancelar um pedido.
//
// Backend (F2 C1): GET/POST /api/admin/credential-reset[/:id/{approve,cancel}].

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Input } from '@aions/ui-primitives';
import { apiFetch } from '../lib/api-client';
import { comiteFirstAccessUrl } from '../lib/spa-urls';

interface PendingItem {
  requestId: string;
  subjectUserId: string;
  subjectEmail: string | null;
  requestedBy?: string;
  expiresAt: number;
}

/** Resultado de uma aprovação executada — link a entregar ao membro. */
interface ExecutedLink {
  requestId: string;
  url: string;
}

export function CredentialResetPending({
  reloadSignal,
  onCountChange,
}: {
  reloadSignal: number;
  /** Informa quantos pedidos estão pendentes (alimenta o card de navegação da
   *  MembersPage). Guardado em ref de propósito: se entrasse nas deps do
   *  `load`, um caller que passasse arrow inline recriaria o callback a cada
   *  render → efeito reexecuta → fetch em loop. Com ref, é seguro para
   *  qualquer caller, não só para os disciplinados. */
  onCountChange?: (pending: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [items, setItems] = useState<PendingItem[]>([]);
  const onCountRef = useRef(onCountChange);
  onCountRef.current = onCountChange;
  const [approveFor, setApproveFor] = useState<string | null>(null);
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executed, setExecuted] = useState<ExecutedLink | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const resp = await apiFetch('/api/admin/credential-reset');
      if (!resp.ok) {
        setItems([]);
        onCountRef.current?.(0);
        return;
      }
      const data = (await resp.json()) as { pending: PendingItem[] };
      setItems(data.pending);
      onCountRef.current?.(data.pending.length);
    } catch {
      setItems([]);
      onCountRef.current?.(0);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadSignal]);

  function mapError(code: string | undefined): string {
    const known = [
      'already_approved',
      'expired',
      'self_reset_forbidden',
      'invalid_totp',
      'request_not_found',
    ];
    const key = code && known.includes(code) ? code : 'generic';
    return t(`members.credreset.pending.errors.${key}`);
  }

  async function approve(item: PendingItem): Promise<void> {
    if (!totp.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/admin/credential-reset/${item.requestId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ totpCode: totp.trim() }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        setError(mapError(body.error));
        return;
      }
      const data = (await resp.json()) as { firstAccessToken?: string; otpAuthUri?: string };
      if (data.firstAccessToken && item.subjectEmail) {
        // TODO(F5-followup): link de first-access no fluxo de RESET aponta sempre ao comitê (role-agnostic).
        // Para apontar por papel (admin/recurso), incluir subjectRoles na lista de pending + na resposta do approve (backend).
        // Decisão Vergueiro: adiado — ganho pequeno (passo manual em caso raro) vs custo de tocar o reset-de-dois-admins (peça sensível F2). Reavaliar pós-go-live se incomodar na prática.
        const url = comiteFirstAccessUrl(data.firstAccessToken, item.subjectEmail, data.otpAuthUri);
        setExecuted({ requestId: item.requestId, url });
      }
      setApproveFor(null);
      setTotp('');
      await load();
    } catch {
      setError(t('members.credreset.pending.errors.generic'));
    } finally {
      setBusy(false);
    }
  }

  async function cancel(item: PendingItem): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/admin/credential-reset/${item.requestId}/cancel`, {
        method: 'POST',
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        setError(mapError(body.error));
        return;
      }
      await load();
    } catch {
      setError(t('members.credreset.pending.errors.generic'));
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      /* clipboard indisponível — admin copia manualmente */
    }
  }

  return (
    <div className="card" data-testid="credreset-pending" style={{ marginBottom: 24 }}>
      <h2 style={{ marginTop: 0 }}>{t('members.credreset.pending.title')}</h2>

      {error && (
        <Alert variant="error" data-testid="credreset-error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {executed && (
        <Alert
          variant="success"
          role="status"
          data-testid="credreset-executed"
          style={{ marginBottom: 16 }}
        >
          <p>{t('members.credreset.pending.link_intro')}</p>
          <p style={{ wordBreak: 'break-all' }}>
            <a href={executed.url} data-testid="credreset-link">
              {executed.url}
            </a>
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void copy(executed.url)}
            data-testid="credreset-copy"
          >
            {copied
              ? t('members.credreset.pending.copied')
              : t('members.credreset.pending.copy_link')}
          </Button>
        </Alert>
      )}

      {items.length === 0 ? (
        <p className="cf-field__help">{t('members.credreset.pending.empty')}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {items.map((item) => (
            <li
              key={item.requestId}
              data-testid={`credreset-item-${item.requestId}`}
              style={{ borderTop: '1px solid var(--color-border)', padding: '12px 0' }}
            >
              <div>
                <strong>{item.subjectEmail ?? item.subjectUserId}</strong>
                <span className="cf-field__help" style={{ marginLeft: 8 }}>
                  {t('members.credreset.pending.requested_by')}: {item.requestedBy ?? '—'}
                </span>
              </div>

              {approveFor === item.requestId ? (
                <div style={{ marginTop: 8 }}>
                  <Input
                    type="text"
                    inputMode="numeric"
                    mono
                    aria-label={t('members.credreset.pending.totp_label')}
                    placeholder={t('members.credreset.pending.totp_label')}
                    value={totp}
                    onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    data-testid={`credreset-totp-${item.requestId}`}
                    style={{ maxWidth: 180 }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Button
                      loading={busy}
                      disabled={!totp.trim() || busy}
                      onClick={() => void approve(item)}
                      data-testid={`credreset-confirm-${item.requestId}`}
                    >
                      {busy
                        ? t('members.credreset.pending.approving')
                        : t('members.credreset.pending.confirm_approve')}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setApproveFor(null);
                        setTotp('');
                      }}
                      disabled={busy}
                    >
                      {t('common.close')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button
                    onClick={() => {
                      setApproveFor(item.requestId);
                      setTotp('');
                      setError(null);
                    }}
                    data-testid={`credreset-approve-${item.requestId}`}
                  >
                    {t('members.credreset.pending.approve')}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => void cancel(item)}
                    disabled={busy}
                    data-testid={`credreset-cancel-${item.requestId}`}
                  >
                    {t('members.credreset.pending.cancel_request')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
